import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { VaStatementOfTruth } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  fullNameReducer,
  statementOfTruthFullName,
} from '~/platform/forms/components/review/PreSubmitSection';
import { setPreSubmit as setPreSubmitAction } from 'platform/forms-system/src/js/actions';
import { querySelectorWithShadowRoot } from 'platform/utilities/ui/webComponents';

function PresubmitInfo({
  formData,
  showError,
  setPreSubmit,
  onSectionComplete,
}) {
  // local state
  const [signatureBlurred, setSignatureBlurred] = useState(false);
  const [inputError, setInputError] = useState(null);
  const [checkboxError, setCheckboxError] = useState(null);
  const [sectionComplete, setSectionComplete] = useState(false);
  const expectedFullName = statementOfTruthFullName(
    formData,
    {
      fullNamePath: 'designatingOfficial.fullName',
    },
    null,
  );

  // event handlers
  const handleCheckboxChange = useCallback(
    event => {
      setPreSubmit('statementOfTruthCertified', event.detail.checked);
    },
    [setPreSubmit],
  );

  const handleInputChange = useCallback(
    event => {
      setPreSubmit('statementOfTruthSignature', event.detail.value);
    },
    [setPreSubmit],
  );

  const handleInputBlur = useCallback(
    () => setSignatureBlurred(true),
    [setSignatureBlurred],
  );

  const removeNoteText = async () => {
    const noteText = await querySelectorWithShadowRoot(
      'p.font-sans-6',
      document.querySelector('va-statement-of-truth'),
    );
    noteText?.setAttribute('style', 'display:none;');
  };

  // effects: need to update formData and errors based on what the user
  // has typed and if they've checked the checkbox
  useEffect(() => {
    // check for name-matching error
    const namesMatch =
      fullNameReducer(formData.statementOfTruthSignature) ===
      fullNameReducer(expectedFullName);
    setInputError(
      (showError || signatureBlurred) && !namesMatch
        ? `Enter your name exactly as it appears on your application: ${expectedFullName}`
        : null,
    );

    // check for checkbox error
    setCheckboxError(
      showError && !formData.statementOfTruthCertified
        ? 'You must certify by checking the box'
        : null,
    );

    const isComplete = namesMatch && formData.statementOfTruthCertified;
    if (sectionComplete !== isComplete) {
      setSectionComplete(isComplete);
      onSectionComplete(isComplete);
    }
  }, [
    formData.statementOfTruthSignature,
    formData.statementOfTruthCertified,
    showError,
    signatureBlurred,
    onSectionComplete,
    expectedFullName,
    sectionComplete,
  ]);

  useEffect(() => {
    const initializeComponent = async () => {
      // Hide "Note" above Certification statement
      await removeNoteText();
    };

    initializeComponent();
  }, []);

  return (
    <VaStatementOfTruth
      heading="Certification statement"
      inputLabel="Your full name"
      inputValue={formData.statementOfTruthSignature}
      checked={formData.statementOfTruthCertified}
      inputMessageAriaDescribedby="I have read and accept the privacy policy."
      onVaInputChange={handleInputChange}
      onVaInputBlur={handleInputBlur}
      onVaCheckboxChange={handleCheckboxChange}
      inputError={inputError}
      checkboxError={checkboxError}
    >
      <p>
        I confirm that the identifying information in this form is accurate and
        has been represented correctly.
      </p>
    </VaStatementOfTruth>
  );
}

PresubmitInfo.propTypes = {
  formData: PropTypes.object.isRequired,
  setPreSubmit: PropTypes.func.isRequired,
  onSectionComplete: PropTypes.func.isRequired,
  showError: PropTypes.bool,
};

const mapDispatchToProps = {
  setPreSubmit: setPreSubmitAction,
};

export { PresubmitInfo as BasePresubmitInfo };

export default connect(null, mapDispatchToProps)(PresubmitInfo);
